// ============================================
// LIGA AJUPAM - Viewer League Context (v6.11.55)
// ============================================
// La liga del que MIRA la app, no la del admin.
//
// `LeagueProvider` resuelve la liga activa del ADMIN: pide `admin_get_my_leagues`
// con un token admin y expone un selector. En la PWA del jugador no se monta —
// no tiene sentido, el jugador no elige liga. El problema es que varios hooks
// compartidos (`useSponsorBanners`, `useShareBackgrounds`) usaban
// `useOptionalCurrentLeague()` como única fuente: en la PWA devolvía null, la RPC
// salía sin `p_league_id`, y `_resolve_target_league(NULL)` lanza excepción
// cuando hay más de una liga `enabled` (hoy hay 3). El `catch` lo convertía en
// "no hay datos" y la pantalla degradaba muda.
//
// Este contexto lleva un solo dato —el `league_id` del equipo del usuario— y se
// monta una vez en App. Cualquier componente compartido, por profundo que esté
// (ej. `PlacaStudio` debajo de 13 callsites distintos), resuelve la liga sin que
// haya que threadear una prop por todo el árbol.
//
// Regla: si NO hay liga, el consumidor NO consulta. Preferimos el default vacío
// a un error silencioso.

import React, { createContext, useContext, useMemo } from 'react';

const ViewerLeagueContext = createContext<string | null>(null);

interface ViewerLeagueProviderProps {
  leagueId: string | null | undefined;
  children: React.ReactNode;
}

export const ViewerLeagueProvider: React.FC<ViewerLeagueProviderProps> = ({ leagueId, children }) => {
  const value = useMemo(() => leagueId ?? null, [leagueId]);
  return <ViewerLeagueContext.Provider value={value}>{children}</ViewerLeagueContext.Provider>;
};

/** Liga del usuario que está mirando. Null fuera del provider (público/pre-login). */
export const useViewerLeagueId = (): string | null => useContext(ViewerLeagueContext);
